import i18n from 'i18next';

/**
 * 5-digit, screen-specific error codes (CB25SES-1804).
 *
 * The first two digits name the screen, the last three the error on it. The
 * backend sends its codes as `metadata.error_code` on go-kratos errors and as
 * `?error_code=` when the sign-in callback fails. The codes below are the ones
 * the frontend raises itself, for failures the backend never sees (form
 * validation, network, timeout) or when a response carries no code.
 *
 * Every code is documented on the Confluence page "Error and Status Codes".
 * Codes are permanent: never reuse or renumber one.
 */
export const ErrorCodes = {
  // 10xxx Sign in (AUTH-U01)
  signInInvalidEmail: '10001',

  // 21xxx Admin Console - Create organisation (AUTH-A01)
  createOrgNameRequired: '21001',
  createOrgAliasRequired: '21002',
  createOrgAliasFormat: '21003',
  createOrgContactEmailRequired: '21004',
  createOrgContactEmailFormat: '21005',
  createOrgDomainRequired: '21006',
  createOrgDomainFormat: '21007',
  createOrgConfigMissing: '21024',

  // 22xxx Admin Console - Organisation list, detail and edit
  orgNotFound: '22002',
  orgUpdateInvalid: '22004',
  orgUpdateConflict: '22005',

  // 23xxx Admin Console - Disable / enable organisation
  orgStatusConflict: '23001',
  orgStatusFailed: '23002',

  // 90xxx Any screen
  sessionExpired: '90001',
  forbidden: '90002',
  network: '90003',
  timeout: '90004',
  unexpected: '90099',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

const FIVE_DIGITS = /^[1-9]\d{4}$/;

/** Returns the error code in a go-kratos error body, if it has one. */
export function errorCodeFromBody(body: unknown): string | undefined {
  const code = (body as { metadata?: { error_code?: unknown } } | null)
    ?.metadata?.error_code;
  return typeof code === 'string' && FIVE_DIGITS.test(code) ? code : undefined;
}

/**
 * Reads the error code from a failed response without consuming its body, so
 * callers can still read the message afterwards.
 */
export async function readErrorCode(
  res: Response,
): Promise<string | undefined> {
  try {
    return errorCodeFromBody(await res.clone().json());
  } catch {
    return undefined;
  }
}

/** Picks a code from a `?error_code=` query value, ignoring anything else. */
export function errorCodeFromQuery(value: string | null): string | undefined {
  return value && FIVE_DIGITS.test(value) ? value : undefined;
}

/** Appends the error code to a message, e.g. "Something went wrong. (Error 90099)". */
export function withErrorCode(message: string, code?: string): string {
  if (!code) return message;
  const text = i18n.t('common:errorWithCode', { message, code });
  // Before i18n is ready (or with the key missing) t gives back nothing useful.
  return text && text !== 'common:errorWithCode' && text !== 'errorWithCode'
    ? text
    : `${message} (${code})`;
}
